import { create } from 'zustand';

// When a hydration-linked habit is completed, the habit store can't show UI, so
// it parks a request here. A global host (HydrationLogPromptHost) renders the
// "how much did you drink?" modal and logs the amount to the water tracker.

/**
 * ⭐ A Nutrition-linked tick (phase 3b-2, owner 2026-10-09): the habit store parks the
 * interval being ticked here instead of completing it; NutritionLogPromptHost asks what
 * was eaten ("Ate as planned · Change meal or portion · Complete habit only"). Cancel
 * changes nothing.
 */
export interface NutritionTickRequest {
  habitId: string;
  habitName: string;
  date: string;
  /** The interval being ticked (`HabitSlot.id`), or null for an unlabelled habit. */
  slotId: string | null;
  slotLabel: string | null;
}

/**
 * ⭐ A Supplements-linked tick (owner, 2026-10-23 — step 6 of the Supplements pass): the same
 * parking, for the supplements habit. SupplementsTickPromptHost asks which doses were taken
 * ("Mark all taken · Complete habit only"); Cancel changes nothing. `slotId` is a time of day
 * (`morning` …) or null for a whole-day tick.
 */
export type SupplementsTickRequest = NutritionTickRequest;

interface HabitDisciplinePromptState {
  hydration: { habitId: string; habitName: string } | null;
  nutrition: NutritionTickRequest | null;
  supplements: SupplementsTickRequest | null;
  requestHydration: (req: { habitId: string; habitName: string }) => void;
  requestNutrition: (req: NutritionTickRequest) => void;
  requestSupplements: (req: SupplementsTickRequest) => void;
  clear: () => void;
  clearNutrition: () => void;
  clearSupplements: () => void;
}

export const useHabitDisciplinePrompt = create<HabitDisciplinePromptState>((set) => ({
  hydration: null,
  nutrition: null,
  supplements: null,
  requestHydration: (req) => set({ hydration: req }),
  requestNutrition: (req) => set({ nutrition: req }),
  requestSupplements: (req) => set({ supplements: req }),
  clear: () => set({ hydration: null }),
  clearNutrition: () => set({ nutrition: null }),
  clearSupplements: () => set({ supplements: null }),
}));
